import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useSessionGuard from 'hooks/useSession';
import AuthLoader from 'components/AuthLoader';

// ==============================|| PERMISSION GUARD ||============================== //

export default function PermissionGuard({ children, module, minPermission = 1 }) {
  const result = useSessionGuard();
  const [userPermission, setUserPermission] = useState(0);
  const [permissionLoaded, setPermissionLoaded] = useState(false);
  
  // Extract the correct properties from the hook result
  const isAuthenticated = result.isLoggedIn ?? false;
  const isLoading = !(result.isInitialized ?? false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Get user permission for the specified module
  // Re-check permissions whenever user object changes (handles permission reloads)
  useEffect(() => {
    if (isAuthenticated) {
      try {
        // Get user data from context
        const userData = result.user || {};
        const permissions = userData.permissions || {};
        const modulePermission = permissions[module] || 0;
        
        setUserPermission(modulePermission);
        setPermissionLoaded(true);
      } catch (error) {
        setUserPermission(0);
        setPermissionLoaded(true);
      }
    }
  }, [isAuthenticated, module, result.user]);

  // Listen for permission reload events
  // This ensures we re-check permissions immediately when they're reloaded
  useEffect(() => {
    const handleReloadPermissions = (event) => {
      // When permissions are reloaded, re-check the permission for this module
      if (isAuthenticated) {
        try {
          // Use permissions from event if available, otherwise from context
          const permissions = event?.detail?.permissions || result.user?.permissions || {};
          const modulePermission = permissions[module] || 0;
          setUserPermission(modulePermission);
        } catch (error) {
          setUserPermission(0);
        }
      }
    };

    window.addEventListener('reloadPermissions', handleReloadPermissions);
    return () => window.removeEventListener('reloadPermissions', handleReloadPermissions);
  }, [isAuthenticated, module, result.user]);

  useEffect(() => {
    // Only redirect if we're not loading, authenticated, and permissions are loaded
    if (!isLoading && isAuthenticated && permissionLoaded) {
      // Check if user has minimum required permission
      if (userPermission < minPermission) {
        // Redirect to dashboard with access denied message
        navigate('/', {
          state: { 
            accessDenied: true, 
            message: `Access denied. You don't have permission to view the ${module} module.` 
          },
          replace: true
        });
      }
    }
  }, [isAuthenticated, isLoading, permissionLoaded, userPermission, minPermission, navigate]);

  // Show loading state while checking authentication or permissions
  if (isLoading || (isAuthenticated && !permissionLoaded)) {
    return <AuthLoader />;
  }

  // Only render children if authenticated and has required permission
  if (!isAuthenticated || userPermission < minPermission) {
    return null; // This prevents flash - redirect will happen in useEffect
  }

  // Render children only when authenticated and has required permission
  return children;
}

PermissionGuard.propTypes = { 
  children: PropTypes.any,
  module: PropTypes.string.isRequired,
  minPermission: PropTypes.number
};
